import React, {FC} from 'react';
import styled from 'styled-components';

import {Skeleton, SkeletonColorSchemesEnum} from '../../../../../../../../src/components/common/skeleton';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface MappingRowProps {
  isLoading?: boolean;
  opacity?: number; // number from 0 - 1
  renderSourceField?: Renderer;
  renderDestinationField?: Renderer;
  renderItem?: Renderer;
}

/*
 * Style.
 */

interface LoadingWrapperProps {
  $opacity?: number;
}

const StyledIconWrapperDiv = styled.div<LoadingWrapperProps>`
  padding: 10px 14px;
  opacity: ${(p) => p.$opacity ?? 1};
  display: flex;
  justify-content: center;
`;

const StyledIcon = styled(Icon)`
  transform: rotate(90deg);
  width: 16px;
  height: 16px;
`;

const StyledFieldWrapperDiv = styled.div`
  overflow: hidden;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 4px 0px;
`;

const StyledSkeletonWrapperDiv = styled.div<LoadingWrapperProps>`
  display: inherit;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 12px;
  opacity: ${(p) => p.$opacity ?? 1};
`;

/*
 * Component.
 */

export const MappingRow: FC<MappingRowProps> = (props) => {
  const theme = useUserTheme();
  const {isLoading, opacity, renderSourceField, renderDestinationField, renderItem} = props;
  if (isLoading) {
    return renderLoadingState(opacity);
  }
  return (
    <>
      {renderSourceField && <StyledFieldWrapperDiv>{renderSourceField()}</StyledFieldWrapperDiv>}
      <StyledIconWrapperDiv>
        <StyledIcon name="arrowUpThickSmall" color={theme.greys.shade70} />
      </StyledIconWrapperDiv>
      {renderDestinationField && <StyledFieldWrapperDiv>{renderDestinationField()}</StyledFieldWrapperDiv>}
      {renderItem && <>{renderItem()}</>}
    </>
  );
};

/*
 * Helpers.
 */

function renderLoadingState(opacity: number = 1) {
  return (
    <>
      <StyledSkeletonWrapperDiv $opacity={opacity}>
        <Skeleton height="16px" scheme={SkeletonColorSchemesEnum.GREY10} />
      </StyledSkeletonWrapperDiv>
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconWrapperDiv $opacity={opacity}>
            <StyledIcon name="arrowUpThickSmall" color={theme.greys.shade40} />
          </StyledIconWrapperDiv>
        )}
      </UserThemeConsumer>
      <StyledSkeletonWrapperDiv $opacity={opacity}>
        <Skeleton height="16px" scheme={SkeletonColorSchemesEnum.GREY10} />
      </StyledSkeletonWrapperDiv>
      <div />
    </>
  );
}
